import type { AssetId } from "@/data/model";
import { useState } from "react";
import type { LayoutChangeEvent } from "react-native";
import { View } from "@/client/ui/View";
import { AssetImage } from "./AssetImage";
import type { ImageCrop, ImageFrameShape } from "./imageCrop";
import { cn } from "tailwind-variants";

interface FramedAssetImageProps {
  assetId: AssetId;
  crop?: ImageCrop | null;
  imageWidth?: number | null;
  imageHeight?: number | null;
  frameShape?: ImageFrameShape;
  className?: string;
  imageClassName?: string;
}

export function FramedAssetImage({
  assetId,
  crop,
  imageWidth,
  imageHeight,
  frameShape = `rect`,
  className,
  imageClassName,
}: FramedAssetImageProps) {
  const [frameSize, setFrameSize] = useState<{
    width: number;
    height: number;
  } | null>(null);

  const hasCropData =
    crop?.kind === `rect` && imageWidth != null && imageHeight != null;

  const rect = hasCropData ? crop.rect : null;
  const shapeClassName = frameShape === `circle` ? `rounded-full` : ``;
  const wrapperClassName = cn(
    `relative overflow-hidden`,
    shapeClassName,
    className,
  );

  if (!hasCropData || rect == null) {
    return (
      <View className={wrapperClassName}>
        <AssetImage
          assetId={assetId}
          className="size-full"
          imageClassName={imageClassName}
        />
      </View>
    );
  }

  return (
    <View
      className={wrapperClassName}
      onLayout={(event: LayoutChangeEvent) => {
        const { width, height } = event.nativeEvent.layout;
        if (width <= 0 || height <= 0) {
          return;
        }
        setFrameSize({ width, height });
      }}
    >
      {frameSize == null ? (
        <AssetImage
          assetId={assetId}
          className="size-full"
          imageClassName={imageClassName}
        />
      ) : (
        (() => {
          const rectWidthPx = rect.width * imageWidth;
          const rectHeightPx = rect.height * imageHeight;
          const scaleX =
            rectWidthPx === 0 ? 1 : frameSize.width / Math.max(rectWidthPx, 1);
          const scaleY =
            rectHeightPx === 0
              ? 1
              : frameSize.height / Math.max(rectHeightPx, 1);
          const scale = Math.max(scaleX, scaleY);

          const scaledWidth = Math.round(imageWidth * scale);
          const scaledHeight = Math.round(imageHeight * scale);
          const styleWidth = scaledWidth;
          const styleHeight = scaledHeight;
          const translateX = Math.round(-rect.x * imageWidth * scale);
          const translateY = Math.round(-rect.y * imageHeight * scale);

          return (
            <AssetImage
              assetId={assetId}
              imageClassName={imageClassName}
              className={`absolute top-0 left-0`}
              contentFit={`fill`}
              style={{
                width: styleWidth,
                height: styleHeight,
                transform: [{ translateX }, { translateY }],
              }}
            />
          );
        })()
      )}
    </View>
  );
}
